import { Link, useNavigate } from "react-router-dom";
import Button from "../components/Button";
import { useState, useEffect } from "react";
import { Check, X, ChevronDown, ChevronUp, HelpCircle, Sparkles, Users, Building2, Building, Rocket, Star, ChevronLeft, ChevronRight, Lock, DollarSign, HardDrive, BookOpen, FileText, Video, Film, Ban, BarChart3, TrendingUp, Gift, Plus, Target, Link as LinkIcon, Globe } from "lucide-react";
import AuthModal from "../components/AuthModal";
import SEO from "../components/SEO";

export default function Pricing() {
  const navigate = useNavigate();
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);
  const [isTableExpanded, setIsTableExpanded] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [selectedTier, setSelectedTier] = useState<'free' | 'pro' | 'pro_plus'>('free');
  const [expandedCards, setExpandedCards] = useState<{[key: string]: boolean}>({
    free: false,
    pro: false,
    pro_plus: false
  });

  const toggleCardExpansion = (tier: string) => {
    setExpandedCards(prev => ({
      ...prev,
      [tier]: !prev[tier]
    }));
  };

  useEffect(() => {
    // Redirect salepage subdomain to main edumi.co pricing page
    const hostname = window.location.hostname;
    if (hostname.includes('saledi.web.app')) {
      window.location.href = 'https://coursedee.web.app/pricing' + window.location.hash;
      return;
    }

    // Scroll to packages section if hash is present
    if (window.location.hash === '#packages') {
      const element = document.getElementById('packages');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, []);
  // FINAL 2-TIER SYSTEM - Pro + Pro+
  const plans = [
    {
      name: "Pro",
      tier: "pro",
      subtitle: "สำหรับผู้สอนเริ่มต้น",
      price: "690",
      period: "/ เดือน",
      periodSuffix: "(รายปีฟรี 2 เดือน)",
      description: "เหมาะสำหรับผู้สอนมือใหม่ที่เริ่มต้นสร้างคอร์ส",
      benefits: [
        { text: "ไม่จำกัดนักเรียน", tooltip: "รองรับนักเรียนไม่จำกัดจำนวน ไม่ต้องกังวลเรื่องจำนวนคน", icon: "Sparkles" },
        { text: "พื้นที่จัดเก็บเริ่มต้น 20 GB", tooltip: "พื้นที่เริ่มต้น 20GB สำหรับวิดีโอและไฟล์" },
        { text: "อัปโหลดได้ทุก format", tooltip: "รองรับทุกรูปแบบวิดีโอ ระบบแปลงให้อัตโนมัติ" },
        { text: "Auto video processing", tooltip: "ประมวลผลวิดีโออัตโนมัติ สร้าง thumbnail และ multiple resolutions" },
        { text: "สร้างคอร์สไม่จำกัด", tooltip: "สร้างคอร์สได้ไม่จำกัดจำนวน" },
        { text: "Dashboard และ Analytics", tooltip: "ดูสถิติการเรียน รายได้ และแนวโน้ม" },
        { text: "ไม่มีค่าธรรมเนียมการขาย", tooltip: "รับเงิน 100% ไม่มีการหักค่าธรรมเนียม" },
        { text: "URL: edumi.co/courses/xxx", tooltip: "URL มาตรฐาน", icon: "LinkIcon" },
        { text: "Add-ons: Path URL (+49฿/เดือน)", tooltip: "อัปเกรดเป็น edumi.co/@username", icon: "DollarSign", addon: true },
        { text: "Add-ons: Subdomain (+99฿/เดือน)", tooltip: "อัปเกรดเป็น username.edumi.co", icon: "DollarSign", addon: true },
        { text: "เพิ่มพื้นที่ได้", tooltip: "Add-on: +10GB (149฿), +25GB (299฿), +50GB (549฿), +100GB (899฿), +200GB (1,499฿)" },
        { text: "จ่ายรายปีประหยัดกว่า", tooltip: "สมัครรายปี ฟรี 1 เดือน" },
      ],
      note: null,
      popular: false,
      badge: null,
      gradient: "from-blue-100 to-cyan-100",
    },
    {
      name: "Pro+",
      tier: "pro_plus",
      subtitle: "สำหรับผู้สอนมืออาชีพ",
      price: "1590",
      period: "/ เดือน",
      periodSuffix: "(รายปีฟรี 2 เดือน)",
      description: "เหมาะสำหรับผู้สอนที่ต้องการฟีเจอร์ครบครันและพื้นที่เยอะ",
      benefits: [
        { text: "ไม่จำกัดนักเรียน", tooltip: "รองรับนักเรียนไม่จำกัดจำนวน ไม่ต้องกังวลเรื่องจำนวนคน", icon: "Sparkles" },
        { text: "30GB พื้นที่จัดเก็บ", tooltip: "พื้นที่เริ่มต้น 30GB สำหรับวิดีโอและไฟล์ (มากกว่า Pro 50%)" },
        { text: "อัปโหลดได้ทุก format", tooltip: "รองรับทุกรูปแบบวิดีโอ ระบบแปลงให้อัตโนมัติ" },
        { text: "Auto video processing", tooltip: "ประมวลผลวิดีโออัตโนมัติ สร้าง thumbnail และ multiple resolutions" },
        { text: "สร้างคอร์สไม่จำกัด", tooltip: "สร้างคอร์สได้ไม่จำกัดจำนวน" },
        { text: "Dashboard และ Analytics", tooltip: "ดูสถิติการเรียน รายได้ และแนวโน้ม" },
        { text: "ไม่มีค่าธรรมเนียมการขาย", tooltip: "รับเงิน 100% ไม่มีการหักค่าธรรมเนียม" },
        { text: "URL: edumi.co/courses/xxx (รายเดือน)", tooltip: "URL มาตรฐาน", icon: "LinkIcon" },
        { text: "Subdomain ฟรี (รายปีเท่านั้น)", tooltip: "username.edumi.co - มูลค่า 1,188฿/ปี (เฉพาะ Pro+ รายปี)", icon: "Globe", highlight: true, yearlyOnly: true },
        { text: "Salepage ฟรี 1 หน้า (รายปีเท่านั้น)", tooltip: "สร้างหน้า Landing Page ฟรี - มูลค่า 1,788฿/ปี (เฉพาะ Pro+ รายปี)", icon: "Gift", highlight: true, yearlyOnly: true },
        { text: "Add-ons: Path URL (+49฿/เดือน)", tooltip: "อัปเกรดเป็น edumi.co/@username (รายเดือน)", icon: "DollarSign", addon: true },
        { text: "Add-ons: Subdomain (+99฿/เดือน)", tooltip: "อัปเกรดเป็น username.edumi.co (รายเดือน)", icon: "DollarSign", addon: true },
        { text: "เพิ่มพื้นที่ได้", tooltip: "Add-on: +10GB (149฿), +25GB (299฿), +50GB (549฿), +100GB (899฿), +200GB (1,499฿)" },
        { text: "จ่ายรายปีประหยัดกว่า", tooltip: "สมัครรายปี ฟรี 1 เดือน" },
        { text: "SEO Optimization", tooltip: "ทำให้คอร์สติด Google Search หาลูกค้าใหม่ผ่าน Organic Search", icon: "Sparkles", highlight: true },
        { text: "White-Label (รายปีเท่านั้น)", tooltip: "ไม่มีโลโก้ EduMi บนเว็บของคุณ เว็บดูเหมือนเป็นของคุณ 100% (เฉพาะ Pro+ รายปี)", icon: "Sparkles", highlight: true, yearlyOnly: true },
      ],
      note: null,
      popular: true,
      badge: "ยอดนิยม",
      gradient: "from-purple-100 to-pink-100",
    },
  ];

  return (
    <>
      <SEO
        title="แพ็กเกจ Pro+ รายเดือน รายปี - SEO Optimization & Sale Page Builder"
        description="แพ็กเกจ Pro+ สำหรับผู้สอนมืออาชีพ เริ่มต้น 1,590฿/เดือน รองรับนักเรียนไม่จำกัด พื้นที่ 30GB พร้อม SEO Optimization ทำให้คอร์สติด Google Search และ Sale Page Builder ฟรี สร้างหน้าขายคอร์สได้ง่าย ๆ"
        keywords="แพ็กเกจ Pro+, Pro+ รายเดือน, Pro+ รายปี, SEO Optimization, Sale Page Builder, แพลตฟอร์มสอนออนไลน์, ขายคอร์สออนไลน์, EduMi Pro+, สร้างคอร์สออนไลน์, ระบบสอนออนไลน์, LMS Thailand, ขายความรู้ออนไลน์, ผู้สอนมืออาชีพ, Google Search คอร์ส"
        canonicalUrl="https://edumi.app/pricing"
      />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50/20 to-pink-50/20 pt-20 pb-16 md:pt-24 md:pb-24">
        {/* Pricing Cards - Modern Grid Layout */}
        <div id="packages" className="max-w-7xl mx-auto px-4 scroll-mt-24">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            เลือกแพ็คเก็จ คอร์สเรียน
          </h1>
          <p className="text-xl text-gray-600 mb-3">
            สร้างคอร์สเรียน มืออาชีพ เพิ่มยอดขายได้ง่ายๆ
          </p>
          <Link 
            to="/subscription/checkout-new?tier=pro_plus_semi_annual"
            className="inline-flex items-center gap-2 text-lg font-semibold text-purple-600 hover:text-purple-700 transition-colors"
          >
            เริ่มต้นทดลองใช้งานฟรี 14 วัน คลิกเลย →
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto mt-12 items-start">
          {plans.map((plan, index) => {
            // Define gradient colors based on plan
            const getGradientClass = () => {
              if (plan.tier === 'free') return 'from-emerald-500 to-cyan-600';
              if (plan.tier === 'pro') return 'from-blue-500 to-cyan-500';
              return 'from-blue-500 to-pink-500';
            };
            
            const isExpanded = expandedCards[plan.tier];
            const maxVisibleItems = 5;
            const visibleBenefits = isExpanded ? plan.benefits : plan.benefits.slice(0, maxVisibleItems);
            const hasMoreItems = plan.benefits.length > maxVisibleItems;
            
            return (
            <div
              key={plan.name}
              className={`relative rounded-3xl transition-all duration-500 overflow-hidden group ${
                plan.popular ? "transform md:scale-105" : ""
              }`}
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
                border: '1px solid rgba(255, 255, 255, 0.18)'
              }}
            >
              {/* Gradient Background Header */}
              <div className={`bg-gradient-to-br ${getGradientClass()} p-8 text-white relative rounded-t-3xl`}>
                {/* Badge */}
                {plan.badge && (
                  <div className="absolute top-4 right-4 bg-white/30 backdrop-blur-md text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg">
                    <Star className="w-3 h-3 fill-current" />
                    <span>{plan.badge}</span>
                  </div>
                )}

                {/* Plan Name */}
                <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                <p className="text-white/90 text-sm mb-6">{plan.subtitle}</p>
                
                {/* Price */}
                <div className="bg-white/20 backdrop-blur-md rounded-2xl p-6 mb-4 shadow-lg">
                  <div className="flex items-baseline justify-center gap-2">
                    <span className="text-5xl md:text-6xl font-bold">
                      {plan.price}
                    </span>
                    <span className="text-xl">฿</span>
                    {plan.tier !== 'free' && <span className="text-lg">{plan.period}</span>}
                  </div>
                  {plan.tier === 'free' && <p className="text-white/90 text-sm mt-2">{plan.period}</p>}
                  {plan.periodSuffix && <p className="text-white/80 text-xs mt-2">{plan.periodSuffix}</p>}
                </div>
                
                <p className="text-white/90 text-sm leading-relaxed">
                  {plan.description}
                </p>
              </div>

              {/* Benefits - White Background with Glassmorphism */}
              <div className="bg-white/60 backdrop-blur-sm p-6">
                <ul className="space-y-3 mb-4">
                  {visibleBenefits.map((benefit, index) => {
                    const tooltipId = `${plan.name}-${index}`;
                    const isOpen = openTooltip === tooltipId;
                    
                    const IconComponent = 
                      benefit.icon === "Sparkles" ? Sparkles :
                      benefit.icon === "Rocket" ? Rocket :
                      benefit.icon === "Globe" ? Globe :
                      benefit.icon === "Gift" ? Gift :
                      benefit.icon === "DollarSign" ? DollarSign :
                      benefit.icon === "LinkIcon" ? LinkIcon :
                      Check;
                    
                    const iconColor = 
                      benefit.highlight ? 'text-blue-600 bg-blue-50' :
                      benefit.addon ? 'text-blue-500 bg-blue-50' :
                      benefit.icon === "Sparkles" ? 'text-yellow-500 bg-yellow-50' :
                      benefit.icon === "Globe" ? 'text-cyan-600 bg-cyan-50' :
                      benefit.icon === "Gift" ? 'text-pink-600 bg-pink-50' :
                      benefit.icon === "DollarSign" ? 'text-blue-500 bg-blue-50' :
                      benefit.icon === "LinkIcon" ? 'text-blue-500 bg-blue-50' :
                      'text-green-500 bg-green-50';
                    
                    return (
                      <li key={index} className="relative group/item">
                        <div className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="flex-shrink-0">
                            <IconComponent className="w-4 h-4 text-green-500" />
                          </div>
                          <span className={`text-sm flex-1 leading-relaxed ${benefit.highlight ? 'text-blue-600 font-semibold' : benefit.addon ? 'text-gray-600' : 'text-gray-700'}`}>
                            {benefit.text}
                            {benefit.yearlyOnly && <span className="inline-block ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-600 rounded-full">Yearly only</span>}
                          </span>
                          <button
                            onClick={() => setOpenTooltip(isOpen ? null : tooltipId)}
                            className="text-gray-400 hover:text-blue-600 transition-colors flex-shrink-0 opacity-0 group-hover/item:opacity-100"
                          >
                            <HelpCircle className="w-4 h-4" />
                          </button>
                        </div>
                        
                        {/* Tooltip */}
                        {isOpen && (
                          <div className="mt-2 ml-11 p-3 bg-gradient-to-br from-purple-50 to-fuchsia-50 border border-blue-200 rounded-xl text-xs text-gray-700 leading-relaxed shadow-lg animate-fadeIn">
                            {benefit.tooltip}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>

                {/* Show More/Less Button */}
                {hasMoreItems && (
                  <button
                    onClick={() => toggleCardExpansion(plan.tier)}
                    className="flex items-center justify-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-semibold mb-4 transition-colors"
                  >
                    {isExpanded ? (
                      <>
                        <span>ดูน้อยลง</span>
                        <ChevronUp className="w-4 h-4" />
                      </>
                    ) : (
                      <>
                        <span>ดูเพิ่มเติม ({plan.benefits.length - maxVisibleItems} รายการ)</span>
                        <ChevronDown className="w-4 h-4" />
                      </>
                    )}
                  </button>
                )}

                {/* Note */}
                {plan.note && (
                  <div className="mb-6 p-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-cyan-200 rounded-xl text-xs text-cyan-800 flex items-center gap-2">
                    <div className="w-6 h-6 rounded-lg bg-cyan-100 flex items-center justify-center flex-shrink-0">
                      <Lock className="w-3 h-3" />
                    </div>
                    <span>{plan.note}</span>
                  </div>
                )}

                {/* CTA Button */}
                <div className="flex justify-center">
                  <button 
                    onClick={() => {
                      if (plan.tier === "free") {
                        setSelectedTier('free');
                        setAuthMode('register');
                        setShowAuthModal(true);
                      } else {
                        navigate(`/subscription/select?plan=${plan.tier}`);
                      }
                    }}
                    className="px-8 py-3 text-base font-bold rounded-full border-2 border-gray-800 text-gray-800 hover:bg-gray-800 hover:text-white transition-all duration-300"
                  >
                    SELECT
                  </button>
                </div>
              </div>
            </div>
          )})}
        </div>
      </div>

      {/* Comparison Table */}
      <section className="max-w-7xl mx-auto px-4 py-20">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-textDark mb-4">
            เปรียบเทียบแพ็กเกจทั้งหมด
          </h2>
          <button
            onClick={() => setIsTableExpanded(!isTableExpanded)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-pink-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
          >
            {isTableExpanded ? (
              <>
                <ChevronUp className="w-5 h-5" />
                ซ่อนตาราง
              </>
            ) : (
              <>
                <ChevronDown className="w-5 h-5" />
                ดูตารางเปรียบเทียบ
              </>
            )}
          </button>
        </div>
        
        {isTableExpanded && (
        <>
        <div className="overflow-x-auto rounded-xl md:rounded-2xl">
          <table className="w-full bg-white shadow-xl overflow-hidden">
            <thead>
              <tr className="bg-gradient-to-r from-blue-600 to-pink-600 text-white">
                <th className="px-2 md:px-6 py-2 md:py-4 text-left font-bold text-xs md:text-base">รายการ</th>
                <th className="px-2 md:px-6 py-2 md:py-4 text-center font-bold text-xs md:text-base">Pro</th>
                <th className="px-2 md:px-6 py-2 md:py-4 text-center font-bold text-xs md:text-base">Pro+</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr className="bg-blue-50">
                <td className="px-2 md:px-6 py-2 md:py-4 font-semibold text-xs md:text-base flex items-center gap-1 md:gap-2"><DollarSign className="w-3 md:w-5 h-3 md:h-5 text-green-600" /> ราคา</td>
                <td className="px-2 md:px-6 py-2 md:py-4 text-center"><span className="text-sm md:text-xl font-bold text-blue-600">฿690</span><span className="text-[10px] md:text-sm text-gray-600">/เดือน</span><br className="hidden md:block"/><span className="text-[8px] md:text-xs text-gray-500 hidden md:inline">(สมัครรายปี ฟรี 1 เดือน)</span></td>
                <td className="px-2 md:px-6 py-2 md:py-4 text-center"><span className="text-sm md:text-xl font-bold text-blue-600">฿1,590</span><span className="text-[10px] md:text-sm text-gray-600">/เดือน</span><br className="hidden md:block"/><span className="text-[8px] md:text-xs text-gray-500 hidden md:inline">(สมัครรายปี ฟรี 1 เดือน)</span></td>
              </tr>
              <tr>
                <td className="px-2 md:px-6 py-2 md:py-4 font-semibold text-xs md:text-base flex items-center gap-1 md:gap-2"><HardDrive className="w-3 md:w-5 h-3 md:h-5 text-blue-600" /> พื้นที่</td>
                <td className="px-2 md:px-6 py-2 md:py-4 text-center"><span className="text-sm md:text-lg font-bold text-blue-600">20 GB</span></td>
                <td className="px-2 md:px-6 py-2 md:py-4 text-center"><span className="text-sm md:text-lg font-bold text-blue-600">30 GB</span></td>
              </tr>
              <tr className="bg-blue-50">
                <td className="px-2 md:px-6 py-2 md:py-4 font-semibold text-xs md:text-base flex items-center gap-1 md:gap-2"><Users className="w-3 md:w-5 h-3 md:h-5 text-blue-600" /> นักเรียน</td>
                <td className="px-2 md:px-6 py-2 md:py-4 text-center"><span className="text-xs md:text-lg font-bold text-blue-600">ไม่จำกัด</span></td>
                <td className="px-2 md:px-6 py-2 md:py-4 text-center"><span className="text-xs md:text-lg font-bold text-blue-600">ไม่จำกัด</span></td>
              </tr>
              <tr>
                <td className="px-2 md:px-6 py-2 md:py-4 font-semibold text-xs md:text-base flex items-center gap-1 md:gap-2"><BookOpen className="w-3 md:w-5 h-3 md:h-5 text-blue-600" /> คอร์ส</td>
                <td className="px-2 md:px-6 py-2 md:py-4 text-center"><span className="text-xs md:text-base font-semibold">ไม่จำกัด</span></td>
                <td className="px-2 md:px-6 py-2 md:py-4 text-center"><span className="text-xs md:text-base font-semibold">ไม่จำกัด</span></td>
              </tr>
              <tr className="bg-gray-50">
                <td className="px-2 md:px-6 py-2 md:py-4 font-semibold text-xs md:text-base flex items-center gap-1 md:gap-2"><FileText className="w-3 md:w-5 h-3 md:h-5 text-teal-600" /> บทเรียน</td>
                <td className="px-2 md:px-6 py-2 md:py-4 text-center"><span className="text-[10px] md:text-sm text-gray-600">ตามความจุ</span></td>
                <td className="px-2 md:px-6 py-2 md:py-4 text-center"><span className="text-[10px] md:text-sm text-gray-600">ตามความจุ</span></td>
              </tr>
              <tr>
                <td className="px-2 md:px-6 py-2 md:py-4 font-semibold text-xs md:text-base flex items-center gap-1 md:gap-2"><Video className="w-3 md:w-5 h-3 md:h-5 text-red-600" /> วิดีโอ</td>
                <td className="px-2 md:px-6 py-2 md:py-4 text-center"><Check className="w-4 md:w-5 h-4 md:h-5 text-green-500 inline" /></td>
                <td className="px-2 md:px-6 py-2 md:py-4 text-center"><Check className="w-4 md:w-5 h-4 md:h-5 text-green-500 inline" /></td>
              </tr>
              <tr className="bg-gray-50">
                <td className="px-2 md:px-6 py-2 md:py-4 font-semibold text-xs md:text-base flex items-center gap-1 md:gap-2"><Film className="w-3 md:w-5 h-3 md:h-5 text-pink-600" /> คุณภาพ</td>
                <td className="px-2 md:px-6 py-2 md:py-4 text-center"><span className="text-[10px] md:text-sm font-semibold">720p</span></td>
                <td className="px-2 md:px-6 py-2 md:py-4 text-center"><span className="text-[10px] md:text-sm font-semibold">720p</span></td>
              </tr>
              <tr>
                <td className="px-2 md:px-6 py-2 md:py-4 font-semibold text-xs md:text-base flex items-center gap-1 md:gap-2"><Ban className="w-3 md:w-5 h-3 md:h-5 text-red-600" /> โฆษณา</td>
                <td className="px-2 md:px-6 py-2 md:py-4 text-center"><span className="text-[10px] md:text-sm text-green-600 font-semibold">ไม่มี</span></td>
                <td className="px-2 md:px-6 py-2 md:py-4 text-center"><span className="text-[10px] md:text-sm text-green-600 font-semibold">ไม่มี</span></td>
              </tr>
              <tr className="bg-blue-50">
                <td className="px-2 md:px-6 py-2 md:py-4 font-semibold text-xs md:text-base flex items-center gap-1 md:gap-2"><BarChart3 className="w-3 md:w-5 h-3 md:h-5 text-blue-600" /> Dashboard</td>
                <td className="px-2 md:px-6 py-2 md:py-4 text-center"><Check className="w-4 md:w-5 h-4 md:h-5 text-green-500 inline" /></td>
                <td className="px-2 md:px-6 py-2 md:py-4 text-center"><Check className="w-4 md:w-5 h-4 md:h-5 text-green-500 inline" /></td>
              </tr>
              <tr>
                <td className="px-2 md:px-6 py-2 md:py-4 font-semibold text-xs md:text-base flex items-center gap-1 md:gap-2"><TrendingUp className="w-3 md:w-5 h-3 md:h-5 text-green-600" /> Analytics</td>
                <td className="px-2 md:px-6 py-2 md:py-4 text-center"><Check className="w-4 md:w-5 h-4 md:h-5 text-green-500 inline" /></td>
                <td className="px-2 md:px-6 py-2 md:py-4 text-center"><Check className="w-4 md:w-5 h-4 md:h-5 text-green-500 inline" /></td>
              </tr>
              <tr className="bg-gray-50">
                <td className="px-2 md:px-6 py-2 md:py-4 font-semibold text-xs md:text-base flex items-center gap-1 md:gap-2"><Rocket className="w-3 md:w-5 h-3 md:h-5 text-blue-600" /> SEO</td>
                <td className="px-2 md:px-6 py-2 md:py-4 text-center"><X className="w-4 md:w-5 h-4 md:h-5 text-red-500 inline" /></td>
                <td className="px-2 md:px-6 py-2 md:py-4 text-center"><Check className="w-4 md:w-5 h-4 md:h-5 text-green-500 inline" /></td>
              </tr>
              <tr>
                <td className="px-2 md:px-6 py-2 md:py-4 font-semibold text-xs md:text-base flex items-center gap-1 md:gap-2"><Gift className="w-3 md:w-5 h-3 md:h-5 text-pink-600" /> Salepage</td>
                <td className="px-2 md:px-6 py-2 md:py-4 text-center"><X className="w-4 md:w-5 h-4 md:h-5 text-red-500 inline" /></td>
                <td className="px-2 md:px-6 py-2 md:py-4 text-center"><Check className="w-4 md:w-5 h-4 md:h-5 text-green-500 inline" /></td>
              </tr>
              <tr className="bg-gray-50">
                <td className="px-2 md:px-6 py-2 md:py-4 font-semibold text-xs md:text-base flex items-center gap-1 md:gap-2"><Plus className="w-3 md:w-5 h-3 md:h-5 text-indigo-600" /> Add-on</td>
                <td className="px-2 md:px-6 py-2 md:py-4 text-center"><Check className="w-4 md:w-5 h-4 md:h-5 text-green-500 inline" /></td>
                <td className="px-2 md:px-6 py-2 md:py-4 text-center"><Check className="w-4 md:w-5 h-4 md:h-5 text-green-500 inline" /></td>
              </tr>
              <tr className="bg-blue-50">
                <td className="px-2 md:px-6 py-2 md:py-4 font-semibold text-xs md:text-base flex items-center gap-1 md:gap-2"><Target className="w-3 md:w-5 h-3 md:h-5 text-blue-600" /> เหมาะกับ</td>
                <td className="px-2 md:px-6 py-2 md:py-4 text-center"><span className="text-[10px] md:text-sm font-semibold text-blue-600">เริ่มต้น</span></td>
                <td className="px-2 md:px-6 py-2 md:py-4 text-center"><span className="text-[10px] md:text-sm font-semibold text-blue-600">มืออาชีพ</span></td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div className="mt-6 md:mt-8 bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4 md:p-6">
          <h3 className="font-bold text-sm md:text-lg text-blue-900 mb-2 md:mb-3 flex items-center gap-2">
            <HelpCircle className="w-4 md:w-5 h-4 md:h-5" />
            หมายเหตุสำคัญ
          </h3>
          <ul className="space-y-1.5 md:space-y-2 text-xs md:text-sm text-blue-900">
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold mt-0.5">•</span>
              <span>Pro (690฿) และ Pro+ (1,590฿) <strong>รองรับนักเรียนไม่จำกัด</strong></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold mt-0.5">•</span>
              <span>Pro เริ่มต้น <strong>20GB</strong> / Pro+ เริ่มต้น <strong>30GB</strong></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold mt-0.5">•</span>
              <span>Add-on เพิ่ม <strong>พื้นที่เท่านั้น</strong> (นักเรียนไม่จำกัดอยู่แล้ว)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold mt-0.5">•</span>
              <span>ระบบ <strong>Auto video processing</strong> รองรับทุก format แปลงให้อัตโนมัติ</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold mt-0.5">•</span>
              <span>เมื่อลบวิดีโอหรือไฟล์ออก พื้นที่จะ <strong>ได้คืนทันที</strong></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold mt-0.5">•</span>
              <span>หากพื้นที่เต็ม ระบบยังใช้งานได้ตามปกติ แต่จะ<strong>ไม่สามารถอัปโหลดไฟล์ใหม่ได้</strong> จนกว่าจะลบไฟล์หรือเพิ่มพื้นที่</span>
            </li>
          </ul>
        </div>
        </>
        )}
      </section>

      {/* FAQ Section - Popular Questions Only */}
      <section className="py-16 md:py-20 px-4 bg-gradient-to-br from-gray-50 to-purple-50/30">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-textDark mb-3">
              คำถามยอดนิยม
            </h2>
            <p className="text-base md:text-lg text-textLight">
              คำตอบที่คุณต้องรู้ก่อนเริ่มใช้งาน
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {/* Question 1 */}
            <div className="group bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">Q</span>
                </div>
                <h3 className="font-bold text-base md:text-lg text-textDark pt-1">
                  นักเรียนเยอะ คิดเงินเพิ่มไหม?
                </h3>
              </div>
              <p className="text-sm md:text-base text-textDark leading-relaxed ml-13">
                <span className="font-bold text-green-600">ไม่คิดเพิ่ม!</span> Pro และ Pro+ รองรับนักเรียนไม่จำกัด คุณจ่ายเพียงค่าพื้นที่จัดเก็บเท่านั้น
              </p>
            </div>

            {/* Question 2 */}
            <div className="group bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">Q</span>
                </div>
                <h3 className="font-bold text-base md:text-lg text-textDark pt-1">
                  พื้นที่เต็มจะเกิดอะไร?
                </h3>
              </div>
              <p className="text-sm md:text-base text-textDark leading-relaxed ml-13">
                ยังสอนได้ตามปกติ แค่ไม่สามารถอัปโหลดไฟล์ใหม่ จนกว่าจะลบไฟล์หรือเพิ่มพื้นที่
              </p>
            </div>

            {/* Question 3 */}
            <div className="group bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-pink-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">Q</span>
                </div>
                <h3 className="font-bold text-base md:text-lg text-textDark pt-1">
                  ลบไฟล์แล้วพื้นที่กลับมาไหม?
                </h3>
              </div>
              <p className="text-sm md:text-base text-textDark leading-relaxed ml-13">
                <span className="font-bold text-blue-600">กลับมาทันที</span> เมื่อลบวิดีโอหรือไฟล์ออก พื้นที่จะกลับมาใช้งานได้ทันที
              </p>
            </div>

            {/* Question 4 */}
            <div className="group bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">Q</span>
                </div>
                <h3 className="font-bold text-base md:text-lg text-textDark pt-1">
                  EduMi เหมาะกับใคร?
                </h3>
              </div>
              <p className="text-sm md:text-base text-textDark leading-relaxed ml-13">
                คนที่ต้องการขายความรู้ของตัวเอง ไม่ต้องสร้างเว็บ ไม่โดนหักส่วนแบ่ง
              </p>
            </div>
          </div>

          {/* CTA to Full FAQ */}
          <div className="text-center mt-10">
            <Link to="/faq">
              <button className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-blue-600 to-pink-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 transform hover:scale-105">
                <span>ดูคำถามเพิ่มเติม</span>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-4xl mx-auto px-4 py-20">
        <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-pink-600 to-blue-700 rounded-3xl p-8 md:p-12 text-white shadow-2xl text-center">
          {/* Animated background elements */}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
            <div className="absolute -top-10 -left-10 w-40 h-40 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute -bottom-10 -right-10 w-60 h-60 bg-pink-300/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
            <div className="absolute top-1/2 left-1/2 w-32 h-32 bg-blue-300/10 rounded-full blur-2xl animate-pulse" style={{animationDelay: '0.5s'}}></div>
          </div>
          
          {/* Content */}
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-4">
              <Sparkles className="w-5 h-5" />
              <span className="text-sm font-semibold">เริ่มต้นฟรี ไม่มีค่าใช้จ่าย</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              พร้อมเริ่มต้นแล้วหรือยัง?
            </h2>
            <p className="text-lg md:text-xl mb-8 opacity-95 max-w-2xl mx-auto">
              สร้างคอร์สออนไลน์ของคุณวันนี้ ไม่ต้องใช้บัตรเครดิต ใช้งานได้ทันที
            </p>
            <Link to="/register">
              <button className="group relative bg-white text-blue-600 hover:bg-gray-50 px-10 md:px-14 py-5 text-lg font-bold rounded-2xl shadow-2xl hover:shadow-purple-500/50 transition-all duration-300 transform hover:scale-105 hover:-translate-y-1">
                <span className="flex items-center gap-2">
                  <Rocket className="w-6 h-6 group-hover:animate-bounce" />
                  เริ่มใช้งานฟรี
                  <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
                </span>
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer Note */}
      <section className="max-w-4xl mx-auto px-4 pb-12">
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 border-2 border-blue-200">
          <h3 className="font-bold text-lg text-textDark mb-3 text-center">หมายเหตุสำคัญ</h3>
          <ul className="space-y-2 text-sm text-textDark">
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span>พื้นที่จัดเก็บใช้สำหรับวิดีโอคอร์สและไฟล์การสอน</span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span>Pro และ Pro+ รองรับนักเรียนไม่จำกัด (Add-on เพิ่มเฉพาะพื้นที่จัดเก็บเท่านั้น)</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        mode={authMode}
        tier={selectedTier}
      />
      </div>
    </>
  );
}
